import type { FontModel } from 'interfaces/app';

type BaselineCacheKey = `${string}|${number}|${number}`; // 'Reenie Beanie|177|1.25'
type FontMetricsCacheKey = `${string}|${number}|${boolean}`; // 'Reenie Beanie|177|false', where the boolean indicates if the font is bold or not

export interface FontBoxMetrics {
	/**
	 * baseline → top of tallest glyph (may include diacritics)
	 */
	ascent: number;
	/**
	 * baseline → bottom of deepest descender
	 */
	descent: number;
	/**
	 * total height of the line box, including extra spacing (e.g. leading)
	 */
	lineHeight: number;
}

export interface FontBoxMetricsOptions {
	fontFamily: string,
	fontModel?: FontModel,
	fontSize: number,
	isBold: boolean,
}

export interface EffectiveLineHeightOptions {
	fontFamily: string;
	fontModel?: FontModel;
	fontSize: number;
	isBold: boolean;
}

export interface FontOptions {
	family: string;
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
}

export type RenderingLineHeightOptions = Omit<EffectiveLineHeightOptions, 'fontSize'>;

const MIN_DESCENDER_SPACE_PX = 1;
const baselineCache = new Map<BaselineCacheKey, number>();
const fontBoxMetricsCache = new Map<FontMetricsCacheKey, FontBoxMetrics>();
let baseliner: HTMLDivElement | null = null;
let baselineMarker: HTMLSpanElement | null = null;
let fontMetricsContext: CanvasRenderingContext2D | null = null;

/**
 * Returns the reusable 2D context used to measure glyph metrics.
 *
 * @returns Canvas 2D context when available; otherwise undefined.
 */
function getFontMetricsContext(): CanvasRenderingContext2D | undefined {
	if (fontMetricsContext) {
		return fontMetricsContext;
	}

	const canvasElement = document.createElement('canvas');
	const context = canvasElement.getContext('2d');

	if (!context) {
		return undefined;
	}

	fontMetricsContext = context;

	return fontMetricsContext;
}

export const DEFAULT_LINEHEIGHT = 1.25;

export const MIN_FONTSIZE = Math.round(pointToPixel(12));

/**
 * Calculates the baseline offset in pixels for a given font, font size, and line height.
 * The baseline offset is the vertical distance from the top of the text container to the
 * text baseline, which is critical for precise text positioning in SVG or other rendering contexts.
 * The function uses a temporary DOM element to measure the baseline and caches the result
 * to avoid redundant calculations.
 *
 * @param fontFamily - The font family to measure the baseline for.
 * @param fontSize - The font size in pixels.
 * @param lineHeight - The line height multiplier (default: {@link DEFAULT_LINEHEIGHT}).
 * @returns The baseline offset in pixels, measured from the top of the text container.
 */
export function getBaselineOffsetPx(
	fontFamily: string,
	fontSize: number,
	lineHeight = DEFAULT_LINEHEIGHT,
): number {
	/**
	 * We use 3 decimals rounding as a middle ground to balance between precision and reduction of
	 * quantization error.
	 * 2 decimals can shift baseline measurement by up to ~0.005 * fontSize px (e.g. ~1.5px at size 300),
	 * it's small but reducible.
	 */
	const normalizedLineHeight = Math.round(lineHeight * 1000) / 1000;
	/**
	 * Create a cache key based on the fontFamily, fontSize, and lineHeight
	 * to store and retrieve previously calculated baseline offsets.
	 */
	const key: BaselineCacheKey = `${fontFamily}|${fontSize}|${normalizedLineHeight}`;

	/**
	 * Check if the baseline offset for this combination is already cached.
	 * If found, return the cached value to avoid recalculation.
	 */
	if (baselineCache.has(key)) {
		return baselineCache.get(key)!;
	}

	if (!baseliner) {
		/**
		 * Create a temporary <div> element to measure the text baseline.
		 * Set styles to ensure accurate measurement:
		 * - Absolute positioning to avoid affecting the page layout.
		 * - Hidden visibility to prevent visual impact.
		 * - Pre-formatted whitespace to preserve text layout.
		 * - Font family, size, and line height as specified.
		 * - Disable ligatures and special font features for consistency.
		 * - Reset padding, margin, and border to eliminate extra space.
		 */
		baseliner = document.createElement('div');
		baseliner.style.position = 'absolute';
		baseliner.style.visibility = 'hidden';
		baseliner.style.whiteSpace = 'pre';
		baseliner.style.textTransform = 'none';
		baseliner.style.font = 'unset';
		baseliner.style.padding = '0';
		baseliner.style.margin = '0';
		baseliner.style.border = '0 solid';
		baseliner.style.left = '-9999px';
		baseliner.style.top = '-9999px';
		/**
		 * Append the wrapper to the document body to enable measurement
		 * of its bounding box and the baseline mark's position.
		 */
		document.body.appendChild(baseliner);
		baseliner.style.fontVariantLigatures = 'no-contextual';
		baseliner.style.fontFeatureSettings = '"calt" 0, "rand" 0';
	}

	baseliner.style.fontFamily = fontFamily;
	baseliner.style.fontSize = `${fontSize}px`;
	baseliner.style.lineHeight = String(normalizedLineHeight);
	baseliner.textContent = 'X';

	if (!baselineMarker) {
		/**
		 * Create a zero-sized <span> element to mark the baseline position.
		 * The span is inline-block to ensure it aligns with the text baseline.
		 */
		baselineMarker = document.createElement('span');
		baselineMarker.style.display = 'inline-block';
		baselineMarker.style.width = '0';
		baselineMarker.style.height = '0';
		baselineMarker.style.textTransform = 'none';
		baselineMarker.style.font = 'unset';
		baselineMarker.style.padding = '0';
		baselineMarker.style.margin = '0';
		baselineMarker.style.border = '0 solid';
	}

	baseliner.appendChild(baselineMarker);
	baselineMarker.style.fontVariantLigatures = 'no-contextual';
	baselineMarker.style.fontFeatureSettings = '"calt" 0, "rand" 0';

	/**
	 * Measure the bounding boxes of the wrapper and baseline mark.
	 * Calculate the offset as the difference between the top of the
	 * baseline mark and the top of the wrapper.
	 */
	const wrapperBox = baseliner.getBoundingClientRect();
	const markBox = baselineMarker.getBoundingClientRect();
	const offset = markBox.top - wrapperBox.top;

	baselineCache.set(
		key,
		offset,
	);

	return offset;
}

/**
 * Resolves a line-height multiplier that is guaranteed to fit the measured
 * glyph box for the provided font and size.
 *
 * @param options - {@link EffectiveLineHeightOptions} to use for calculating the effective line-height.
 * @param options.fontFamily - Font family to measure.
 * @param options.fontModel - Font model to check if the font is loaded.
 * @param options.fontSize - Font size in pixels.
 * @param options.isBold - Whether to measure the bold variant of the font.
 * @returns Effective line-height multiplier that avoids descender clipping.
 */
export function getEffectiveLineHeight(options: EffectiveLineHeightOptions): number {
	const {
		fontFamily,
		fontModel,
		fontSize,
		isBold,
	} = options;

	if (!fontModel?._loaded) {
		return DEFAULT_LINEHEIGHT;
	}

	const {
		ascent,
		descent,
		lineHeight: renderingLineHeight,
	} = getFontBoxMetrics({
		fontFamily,
		fontModel,
		fontSize,
		isBold,
	});
	const minimumLineHeight = ((ascent + descent) + MIN_DESCENDER_SPACE_PX) / fontSize;
	let minimumLineHeightFromBaseline = renderingLineHeight;

	/**
	 * Keep enough space below the baseline for descenders.
	 * This protects fonts where baseline placement within the line box is low.
	 */
	try {
		const baselineOffset = getBaselineOffsetPx(
			fontFamily,
			fontSize,
			renderingLineHeight,
		);
		minimumLineHeightFromBaseline = (
			(baselineOffset + descent) + MIN_DESCENDER_SPACE_PX
		) / fontSize;
	} catch {
		/**
		 * No further action required when baseline measurement is unavailable.
		 */
	}

	return Math.max(
		renderingLineHeight,
		minimumLineHeight,
		minimumLineHeightFromBaseline,
	);
}

/**
 * Measures the glyph box (ascent/descent) for the provided font and size.
 *
 * @param options - {@link FontBoxMetricsOptions} to use for calculating the font box metrics.
 * @param options.fontFamily - Font family to measure.
 * @param options.fontModel - Font model to check if the font is loaded.
 * @param options.fontSize - Font size in pixels.
 * @param options.isBold - Whether to measure the bold variant of the font.
 * @returns Measured glyph-box metrics for ascent and descent.
 */
export function getFontBoxMetrics(options: FontBoxMetricsOptions): FontBoxMetrics {
	const {
		fontFamily,
		fontSize,
		isBold,
		fontModel,
	} = options;
	const key: FontMetricsCacheKey = `${fontFamily}|${fontSize}|${isBold}`;

	if (fontBoxMetricsCache.has(key)) {
		return fontBoxMetricsCache.get(key)!;
	}

	const fallbackMetrics: FontBoxMetrics = {
		ascent: fontSize * 0.8,
		descent: fontSize * 0.2,
		lineHeight: DEFAULT_LINEHEIGHT,
	};

	if (!fontModel?._loaded) {
		return fallbackMetrics;
	}

	const context = getFontMetricsContext();

	if (!context) {
		return fallbackMetrics;
	}

	context.font = `${fontSize}px ${fontFamily}`;
	context.textAlign = 'left';
	context.textBaseline = 'alphabetic';
	const textMetrics = context.measureText('abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ');
	const {
		actualBoundingBoxAscent,
		actualBoundingBoxDescent,
	} = textMetrics;
	const hasValidBoxMetrics = (
		Number.isFinite(actualBoundingBoxAscent)
		&& Number.isFinite(actualBoundingBoxDescent)
		&& actualBoundingBoxAscent > 0
		&& actualBoundingBoxDescent > 0
	);
	const resolvedMetrics: FontBoxMetrics = (
		hasValidBoxMetrics
			? {
				ascent: actualBoundingBoxAscent,
				descent: actualBoundingBoxDescent,
				/**
				 * Since the default line-height multiplier used to give some breath space is 1.25,
				 * we apply that same multiplier to the measured glyph box to ensure that same amount
				 * of breath space is preserved.
				 */
				lineHeight: ((actualBoundingBoxAscent + actualBoundingBoxDescent) / fontSize) * DEFAULT_LINEHEIGHT,
			}
			: fallbackMetrics
	);

	fontBoxMetricsCache.set(
		key,
		resolvedMetrics,
	);

	return resolvedMetrics;
}

export function getFontFamily(options: FontOptions): string {
	const {
		family,
		bold,
		italic,
	} = options;
	let font = family;

	if (
		bold
		&& italic
	) {
		font += ' BoldItalic';
	} else if (bold) {
		font += ' Bold';
	} else if (italic) {
		font += ' Italic';
	}

	return font;
}

export function pixelToPoint(
	pixels: number,
	ppi = 150,
): number {
	return pixels * 72 / ppi;
}

export function pointToPixel(
	points: number,
	ppi = 150,
): number {
	return points * ppi / 72;
}
